import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Cpu,
  MemoryStick,
  ExternalLink,
  Power,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import clsx from 'clsx';
import { api } from '../../api/client';

type ContainerStatus = 'healthy' | 'warning' | 'unhealthy' | 'stopped';

interface ContainerInfo {
  name: string;
  image: string;
  status: ContainerStatus;
  uptime: string;
  cpu_pct: number;
  mem_pct: number;
  url?: string;
  health_endpoint?: string;
}

interface HealthResponse {
  containers: ContainerInfo[];
  checked_at: string;
}

const SERVICE_URLS: Record<string, string> = {
  'api-gateway': 'https://teleclaw.ncide.com/health',
  postgres: '',
  redis: '',
  rabbitmq: 'https://teleclaw.ncide.com/rabbitmq',
  'analytics-svc': 'https://teleclaw.ncide.com/api/analytics/health',
  'content-svc': 'https://teleclaw.ncide.com/api/content/health',
  'farm-svc': 'https://teleclaw.ncide.com/api/farm/health',
  scheduler: 'https://teleclaw.ncide.com/api/scheduler/health',
  'telegram-connector': 'https://teleclaw.ncide.com/api/telegram/health',
};

async function fetchHealth(): Promise<HealthResponse> {
  const { data } = await api.get<HealthResponse>('/admin/health');
  return data;
}

function StatusIcon({ status }: { status: ContainerStatus }): JSX.Element {
  if (status === 'healthy') return <CheckCircle2 size={18} className="text-emerald-500" />;
  if (status === 'warning') return <AlertCircle size={18} className="text-yellow-500" />;
  if (status === 'stopped') return <Power size={18} className="text-slate-400" />;
  return <XCircle size={18} className="text-red-500" />;
}

function StatusBadge({ status }: { status: ContainerStatus }): JSX.Element {
  const map: Record<ContainerStatus, string> = {
    healthy: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-yellow-100 text-yellow-700',
    unhealthy: 'bg-red-100 text-red-700',
    stopped: 'bg-slate-100 text-slate-500',
  };
  return (
    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-semibold capitalize', map[status])}>
      {status}
    </span>
  );
}

function CpuBar({ value }: { value: number }): JSX.Element {
  const color =
    value >= 90 ? 'bg-red-500' : value >= 70 ? 'bg-yellow-400' : 'bg-emerald-500';
  return (
    <div>
      <div className="flex justify-between text-xs text-brand-muted mb-0.5">
        <span className="flex items-center gap-1">
          <Cpu size={11} /> CPU
        </span>
        <span>{value}%</span>
      </div>
      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={clsx('h-full rounded-full transition-all', color)} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function MemBar({ value }: { value: number }): JSX.Element {
  const color =
    value >= 90 ? 'bg-red-500' : value >= 70 ? 'bg-yellow-400' : 'bg-blue-500';
  return (
    <div>
      <div className="flex justify-between text-xs text-brand-muted mb-0.5">
        <span className="flex items-center gap-1">
          <MemoryStick size={11} /> Mem
        </span>
        <span>{value}%</span>
      </div>
      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={clsx('h-full rounded-full transition-all', color)} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function ContainerCard({ container }: { container: ContainerInfo }): JSX.Element {
  const borderColor = {
    healthy: 'border-emerald-200',
    warning: 'border-yellow-200',
    unhealthy: 'border-red-200',
    stopped: 'border-slate-200',
  }[container.status];

  const bgColor = {
    healthy: 'bg-emerald-50/40',
    warning: 'bg-yellow-50/40',
    unhealthy: 'bg-red-50/40',
    stopped: 'bg-slate-50',
  }[container.status];

  const serviceUrl = container.url ?? SERVICE_URLS[container.name];

  return (
    <div className={clsx('rounded-xl border p-4 space-y-3', borderColor, bgColor)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <StatusIcon status={container.status} />
          <div className="min-w-0">
            <div className="font-semibold text-sm text-brand-ink truncate">{container.name}</div>
            <div className="text-xs text-brand-muted truncate">{container.image}</div>
          </div>
        </div>
        {serviceUrl && (
          <a
            href={serviceUrl}
            target="_blank"
            rel="noreferrer"
            className="p-1 rounded hover:bg-white/60 text-brand-muted hover:text-brand-primary transition-colors shrink-0"
            title="Open endpoint"
          >
            <ExternalLink size={13} />
          </a>
        )}
      </div>

      {/* Status & uptime */}
      <div className="flex items-center justify-between">
        <StatusBadge status={container.status} />
        <div className="flex items-center gap-1 text-xs text-brand-muted">
          <Clock size={12} />
          <span>{container.uptime}</span>
        </div>
      </div>

      {/* Resource bars */}
      {container.status !== 'stopped' && (
        <div className="space-y-2">
          <CpuBar value={container.cpu_pct} />
          <MemBar value={container.mem_pct} />
        </div>
      )}
    </div>
  );
}

export function AdminHealth(): JSX.Element {
  const [autoRefresh, setAutoRefresh] = useState(true);

  const healthQ = useQuery({
    queryKey: ['admin', 'health'],
    queryFn: fetchHealth,
    refetchInterval: autoRefresh ? 30_000 : false,
  });

  const containers = healthQ.data?.containers ?? [];
  const checkedAt = healthQ.data?.checked_at;

  const handleRefresh = useCallback((): void => {
    void healthQ.refetch();
  }, [healthQ]);

  // Keyboard shortcut R
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'r' && !e.ctrlKey && !e.metaKey && (e.target as HTMLElement).tagName !== 'INPUT') {
        handleRefresh();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleRefresh]);

  const counts = containers.reduce(
    (acc, c) => {
      acc[c.status] = (acc[c.status] ?? 0) + 1;
      return acc;
    },
    {} as Record<ContainerStatus, number>,
  );

  return (
    <div className="p-6 space-y-5">
      {/* Title bar */}
      <div className="flex items-center gap-4 flex-wrap">
        <h1 className="text-2xl font-bold text-brand-ink">Service Health</h1>

        <div className="flex items-center gap-3 ml-auto flex-wrap">
          {checkedAt && (
            <span className="text-xs text-brand-muted">
              Last checked: {new Date(checkedAt).toLocaleTimeString()}
            </span>
          )}

          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition',
              autoRefresh
                ? 'border-brand-primary text-brand-primary bg-brand-primary/5'
                : 'border-slate-200 text-brand-muted',
            )}
          >
            {autoRefresh ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
            Auto-refresh 30s
          </button>

          <button
            onClick={handleRefresh}
            disabled={healthQ.isFetching}
            className="flex items-center gap-1.5 btn-primary py-1.5 text-sm"
          >
            <RefreshCw
              size={15}
              className={clsx(healthQ.isFetching && 'animate-spin')}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary row */}
      {!healthQ.isLoading && containers.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {(
            [
              { status: 'healthy', label: 'Healthy', color: 'text-emerald-700 bg-emerald-100' },
              { status: 'warning', label: 'Warning', color: 'text-yellow-700 bg-yellow-100' },
              { status: 'unhealthy', label: 'Unhealthy', color: 'text-red-700 bg-red-100' },
              { status: 'stopped', label: 'Stopped', color: 'text-slate-600 bg-slate-100' },
            ] as const
          ).map(({ status, label, color }) =>
            counts[status] ? (
              <span
                key={status}
                className={clsx('px-3 py-1 rounded-full text-sm font-semibold', color)}
              >
                {counts[status]} {label}
              </span>
            ) : null,
          )}
        </div>
      )}

      {/* Container grid */}
      {healthQ.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="h-44 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : healthQ.isError ? (
        <div className="card text-red-600 flex items-center gap-2">
          <XCircle size={18} /> Failed to load health data.
          <button onClick={handleRefresh} className="ml-2 underline text-sm">
            Retry
          </button>
        </div>
      ) : containers.length === 0 ? (
        <div className="card text-brand-muted">No container data available.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {containers.map((c) => (
            <ContainerCard key={c.name} container={c} />
          ))}
        </div>
      )}
    </div>
  );
}
